import React, { useCallback } from 'react';
import GenericSearchbar, { ParsedFilter } from '../../../../../ui-components/Searchbar/GenericSearchbar';
import { RouteFilters, DEFAULT_ROUTE_FILTERS } from '../../utils/filterModel';

export interface RouteFilterReflectorOption {
  id: string;
  name: string;
}

interface RouteFilterToolbarProps {
  /** Receives the RouteFilters derived from the searchbar's parsed key:value filters. */
  onChange: (next: RouteFilters) => void;
  /** Cluster members, used to resolve a `reflector = <name>` filter to its subId. */
  reflectorOptions: RouteFilterReflectorOption[];
  /** Whether to offer the `reflector` filter (Table mode on a cluster). */
  includeReflector: boolean;
  /** Distinct prefixes (`prefix|family`) matching the active filters, shown in the count line. */
  matchedCount: number;
  /** Distinct prefixes in the active view before filtering. */
  totalCount: number;
}

const normalizeKey = (key: string) => key.trim().toLowerCase().replace(/[\s_-]/g, '');

/** Spellings accepted for the discovery-tag filter. */
const TAG_FILTER_KEYS = ['tag', 'tags', 'discoveredby'];

const isTagFilterKey = (normalizedKey: string) => TAG_FILTER_KEYS.includes(normalizedKey);

/**
 * Whether a bare (keyless) term is really the user reaching for the tag filter.
 *
 * `GenericSearchbar` only produces a `key` when something follows the separator
 * (`parseInputToFilter`), so picking the `tag` suggestion — or typing `tag` and hitting Enter —
 * arrives here as the keyless term "tag" or "tag =". Left alone it becomes a text search for the
 * literal word, which matches nothing and reads as "there are no tagged routes". Treat it as
 * "any tag-discovered destination" instead.
 */
const isBareTagTerm = (value: string) => isTagFilterKey(normalizeKey(value.replace(/[:=]\s*$/, '')));

/**
 * Translates the searchbar's parsed `key:value` filters into the flat RouteFilters model.
 * The full filter array is rebuilt each change, so removing a chip clears that field.
 * Last value wins when a key repeats. A bare value (no key) is a general search — it matches
 * prefix, neighbor IP, AS path, next hop, and reflector name (see `applyRouteFilters`'s
 * `search` handling), not just prefix — otherwise typing a bare neighbor IP like "192.0.2.1"
 * would never match anything, since no route's prefix ever contains a neighbor IP.
 */
export function buildFilters(parsed: ParsedFilter[], reflectorOptions: RouteFilterReflectorOption[]): RouteFilters {
  const next: RouteFilters = { ...DEFAULT_ROUTE_FILTERS };

  for (const p of parsed) {
    const value = p.value.trim();
    const key = p.key?.trim() ?? '';

    if (!key) {
      if (!value) continue;
      if (isBareTagTerm(value)) {
        next.tag = ''; // presence-only: every tag-discovered destination
        continue;
      }
      next.search = value; // bare text → general search across prefix/neighbor/AS-path/etc.
      continue;
    }

    const normalizedKey = normalizeKey(key);
    // `tag` is the one filter whose empty value is meaningful ("has any tag"); for every other
    // key an empty value just means the user hasn't typed the term yet.
    if (!value && !isTagFilterKey(normalizedKey)) continue;

    switch (normalizedKey) {
      case 'prefix':
      case 'cidr':
        next.prefix = value;
        break;
      case 'neighbor':
      case 'neighborip':
      case 'ip':
        next.neighborIp = value;
        break;
      case 'asn':
      case 'as':
      case 'neighboras':
        next.neighborAs = value;
        break;
      case 'localpref':
      case 'lp':
        next.localPref = value;
        break;
      case 'family': {
        const fv = value.toLowerCase();
        next.family = fv.includes('vpn')
          ? 'vpnv4'
          : fv.includes('ipv6') || fv.includes('v6')
            ? 'ipv6-unicast'
            : fv.includes('ipv4') || fv.includes('unicast')
              ? 'ipv4-unicast'
              : 'all';
        break;
      }
      case 'tag':
      case 'tags':
      case 'discoveredby':
        next.tag = value;
        break;
      case 'reflector':
      case 'ctxr': {
        const match = reflectorOptions.find(
          (o) => o.id === value || o.name.toLowerCase().includes(value.toLowerCase())
        );
        next.reflectorId = match ? match.id : null;
        break;
      }
      default:
        next.search = value; // unrecognized key → fall back to general search
    }
  }

  return next;
}

/**
 * Single search bar that surfaces every available route filter as a typed suggestion
 * (prefix, neighbor, asn, local-pref, family, tag, and reflector in cluster Table mode), so
 * users discover the filters without a row of separate inputs. Committed chips + the live
 * term are mapped to the shared RouteFilters and applied to topology, table and metrics.
 */
export const RouteFilterToolbar: React.FC<RouteFilterToolbarProps> = ({
  onChange,
  reflectorOptions,
  includeReflector,
  matchedCount,
  totalCount
}) => {
  const suggestedKeys = [
    'prefix',
    'neighbor',
    'asn',
    'local-pref',
    'family',
    'tag',
    ...(includeReflector ? ['reflector'] : [])
  ];

  const handleFiltersChange = useCallback(
    (parsed: ParsedFilter[]) => {
      onChange(buildFilters(parsed, reflectorOptions));
    },
    [onChange, reflectorOptions]
  );

  // Lives inside the input's right edge rather than on its own line, so the whole control bar
  // fits one row. Wording is unchanged from the previous count line on purpose: these are
  // distinct prefixes (`prefix|family`), not route rows.
  const countLabel =
    matchedCount === totalCount
      ? `${totalCount} prefixes`
      : `${matchedCount} of ${totalCount} prefixes match filters`;

  return (
    <GenericSearchbar
      placeholder="Filter routes — e.g. prefix = 10.0.0.0/16, neighbor = 192.0.2.1, tag = RT:100"
      suggestedKeys={suggestedKeys}
      onFiltersChange={handleFiltersChange}
      liveFiltering
      size="sm"
      inputMaxWidth="100%"
      trailingContent={countLabel}
    />
  );
};

export default RouteFilterToolbar;
